import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from "react";
import clsx from "clsx";
// Assuming these are defined elsewhere in your project
import masterApiCall from "shared/services/MasterDataApi";
import { useLoading } from "shared/contexts/Loading";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import {
    Box,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description"; // Generic file icon for non-leaf nodes
import AnnotationOverlay from "./AnnotationOverlay";

// --- Configuration for Multi-Select (MUI Styling) ---
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 200, // Reduced width for compactness
        },
    },
};

// --- Multi-Select Filter Component (Unchanged) ---
const MultiSelectFilter = ({
    label,
    name,
    selectedValues,
    onChange,
    options,
    isActive,
}) => {
    const handleClear = (e) => {
        e.stopPropagation();
        onChange(name, []);
    };

    const selectStyle = isActive
        ? {}
        : {
            backgroundColor: "#f5f5f5",
            cursor: "not-allowed",
        };

    return (
        <FormControl
            sx={{
                m: 0,
                flexGrow: 1,
                minWidth: 100,
                pointerEvents: isActive ? "auto" : "none",
            }}
            size="small"
            disabled={!isActive}
        >
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`${name}-label`}
                id={`${name}-select`}
                multiple
                value={selectedValues}
                onChange={(e) => onChange(name, e.target.value)}
                input={
                    <OutlinedInput
                        id={`${name}-chip-input`}
                        label={label}
                        style={selectStyle}
                    />
                }
                renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.length === options.length ? (
                            <Chip label={`All ${label}`} size="small" variant="outlined" />
                        ) : selected.length > 0 ? (
                            <Chip
                                label={`${selected.length} Selected`}
                                size="small"
                                color="primary"
                            />
                        ) : null}
                    </Box>
                )}
                MenuProps={MenuProps}
                endAdornment={
                    selectedValues.length > 0 && isActive ? (
                        <button
                            onClick={handleClear}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "0 8px",
                                display: "flex",
                                alignItems: "center",
                            }}
                            aria-label={`Clear ${label} filter`}
                        >
                            <ClearIcon fontSize="small" color="action" />
                        </button>
                    ) : null
                }
            >
                {options.map((option) => (
                    <MenuItem
                        key={option}
                        value={option}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        {option}
                        {selectedValues.includes(option) && (
                            <CheckIcon fontSize="small" color="primary" />
                        )}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

// --- Utility Function for Color Coding ---
const getFileColorClass = (type) => {
    switch (type) {
        case "PDF":
            return "text-red-600 dark:text-red-400";
        case "VIDEO":
            return "text-orange-600 dark:text-orange-400";
        case "VIRTUAL_LAB":
            return "text-green-600 dark:text-green-400";
        case "SIMULATION":
            return "text-yellow-600 dark:text-yellow-400";
        case "QUIZ":
            return "text-purple-600 dark:text-purple-400";
        case "CONCEPT":
            return "text-yellow-600 dark:text-yellow-400"; // New color for Concepts
        default:
            return "text-pink-600 dark:text-pink-400";
    }
};

// --- Main Component ---
const ResourcesContent = ({ navigateTo }) => {
    const [chapters, setChapters] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [selectedPath, setSelectedPath] = useState([]);
    const [showConcept, setShowConcept] = useState(false);
    // NEW STATE: To hold the ID of the chapter whose concepts are being viewed
    const [selectedChapterId, setSelectedChapterId] = useState(null);

    const initialFilters = {
        type: [],
        class: [],
        subject: [],
        chapter: [],
        searchKeyword: "",
    };
    const [filters, setFilters] = useState(initialFilters);

    const [error, setError] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    // const [isDarkMode, setIsDarkMode] = useState(false);
    const [isTreeOpen, setIsTreeOpen] = useState(true);
    const { startLoading, stopLoading } = useLoading();
    const iframeContainerRef = useRef(null);
    const [expandedItems, setExpandedItems] = useState([]);

    // Determine the active filtering mode
    const isSearchMode = !!filters.searchKeyword;
    const isCategoricalMode =
        !isSearchMode &&
        (filters.type.length > 0 ||
            filters.class.length > 0 ||
            filters.subject.length > 0 ||
            filters.chapter.length > 0);

    // Centralized Filter Change Handler: ensures mutex logic and resets dependent filters (Unchanged)
    const handleFilterChange = useCallback(
        (name, value) => {
            setFilters((prevFilters) => {
                let newFilters = { ...prevFilters };

                if (name === "searchKeyword") {
                    newFilters = { ...initialFilters, searchKeyword: value };
                } else if (Array.isArray(value)) {
                    if (value.length > 0) {
                        newFilters = { ...prevFilters, searchKeyword: "", [name]: value };
                    } else {
                        newFilters = { ...prevFilters, [name]: value };
                    }

                    if (
                        (name === "class" || name === "subject") &&
                        prevFilters.chapter.length > 0
                    ) {
                        newFilters.chapter = [];
                    }
                } else {
                    newFilters = { ...prevFilters, [name]: value };
                }
                return newFilters;
            });
        },
        [initialFilters]
    );

    // Handle search input change (Unchanged)
    const handleSearchChange = useCallback(
        (e) => {
            handleFilterChange("searchKeyword", e.target.value);
        },
        [handleFilterChange]
    );

    // --- Data Fetching Effects (Unchanged) ---
    useEffect(() => {
        masterApiCall(
            "/chapters",
            null,
            "GET",
            null,
            startLoading,
            stopLoading,
            null
        )
            .then((data) => setChapters(data || []))
            .catch((error) => {
                console.error("Error fetching chapters:", error);
                setError(true);
            });
    }, [startLoading, stopLoading]);

    useEffect(() => {
        if (chapters.length === 0 && materials.length > 0) return;

        masterApiCall(
            "/study-materials",
            null,
            "GET",
            null,
            startLoading,
            stopLoading,
            null
        )
            .then((data) => {
                const chapterMap = chapters.reduce((acc, chapter) => {
                    if (chapter?.id) {
                        acc[chapter.id] = chapter;
                    }
                    return acc;
                }, {});

                const materialsWithValidatedTags = (data || []).map((material) => ({
                    ...material,
                    chapter: chapterMap[material.chapter?.id],
                    tags: Array.isArray(material.tags)
                        ? material.tags.filter((tag) => tag && typeof tag.name === "string")
                        : [],
                }));
                setMaterials(materialsWithValidatedTags);
            })
            .catch((error) => {
                console.error("Error fetching study materials:", error);
                setError(true);
            });
    }, [chapters, startLoading, stopLoading]);

    // // ... (Other useEffects remain the same) ...
    // useEffect(() => {
    //     if (isDarkMode) {
    //         document.documentElement.classList.add("dark");
    //     } else {
    //         document.documentElement.classList.remove("dark");
    //     }
    // }, [isDarkMode]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

    // --- Filtering Options and Logic (Unchanged) ---
    const allFilterOptions = useMemo(() => {
        const uniqueOptions = {
            classes: new Set(),
            subjects: new Set(),
            chapters: new Set(),
            types: new Set(["CONCEPT"]), // ADD CONCEPT type here
        };

        let materialsForChapterFilter = materials;
        const selectedClasses = filters.class;
        const selectedSubjects = filters.subject;

        if (selectedClasses.length > 0) {
            materialsForChapterFilter = materialsForChapterFilter.filter((m) =>
                selectedClasses.includes(
                    m.chapter?.classStandard?.name || "Uncategorized Class"
                )
            );
        }

        if (selectedSubjects.length > 0) {
            materialsForChapterFilter = materialsForChapterFilter.filter((m) =>
                selectedSubjects.includes(
                    m.chapter?.subject?.name || "Uncategorized Subject"
                )
            );
        }

        materials.forEach((m) => {
            uniqueOptions.classes.add(
                m.chapter?.classStandard?.name || "Uncategorized Class"
            );
            uniqueOptions.subjects.add(
                m.chapter?.subject?.name || "Uncategorized Subject"
            );
            if (m.type) uniqueOptions.types.add(m.type);
        });

        materialsForChapterFilter.forEach((m) => {
            uniqueOptions.chapters.add(m.chapter?.name || "Uncategorized Chapter");
        });

        return {
            classes: [...uniqueOptions.classes].sort(),
            subjects: [...uniqueOptions.subjects].sort(),
            chapters: [...uniqueOptions.chapters].sort(),
            types: [...uniqueOptions.types].sort(),
        };
    }, [materials, filters.class, filters.subject]);

    // --- Memoized filter logic for the actual Tree View (UPDATED for Concepts) ---
    const getGroupedData = useMemo(() => {
        let filteredMaterials = materials;
        const {
            type,
            class: selectedClass,
            subject,
            chapter,
            searchKeyword,
        } = filters;

        if (isSearchMode) {
            const lowercasedKeyword = searchKeyword.toLowerCase();
            filteredMaterials = filteredMaterials.filter(
                (material) =>
                    material.tags.some((tag) =>
                        tag.name.toLowerCase().includes(lowercasedKeyword)
                    ) ||
                    material.title.toLowerCase().includes(lowercasedKeyword) ||
                    material.description?.toLowerCase().includes(lowercasedKeyword)
            );
        } else if (isCategoricalMode) {
            if (type.length > 0) {
                // If only 'CONCEPT' is selected, don't filter out materials yet, 
                // as 'CONCEPT' nodes are virtual and injected later.
                if (type.length > 1 || !type.includes("CONCEPT")) {
                    filteredMaterials = filteredMaterials.filter((material) =>
                        type.includes(material.type)
                    );
                }
            }
            if (selectedClass.length > 0) {
                filteredMaterials = filteredMaterials.filter((material) =>
                    selectedClass.includes(
                        material.chapter?.classStandard?.name || "Uncategorized Class"
                    )
                );
            }
            if (subject.length > 0) {
                filteredMaterials = filteredMaterials.filter((material) =>
                    subject.includes(
                        material.chapter?.subject?.name || "Uncategorized Subject"
                    )
                );
            }
            if (chapter.length > 0) {
                filteredMaterials = filteredMaterials.filter((material) =>
                    chapter.includes(material.chapter?.name || "Uncategorized Chapter")
                );
            }
        }

        const sortChildrenRecursively = (nodes) => {
            const getChapterNumber = (label) => {
                const match = label.match(/Chapter:\s*\d+\s*-\s*Chapter\s*(\d+)/i);
                if (match) {
                    return parseInt(match[1], 10);
                }
                const simpleMatch = label.match(/(\d+)/);
                return simpleMatch ? parseInt(simpleMatch[1], 10) : Infinity;
            };

            nodes.sort((a, b) => {
                const aIsChapterNode = a.label.startsWith("Chapter:");
                const bIsChapterNode = b.label.startsWith("Chapter:");

                if (aIsChapterNode && bIsChapterNode) {
                    const numA = getChapterNumber(a.label);
                    const numB = getChapterNumber(b.label);

                    if (numA !== Infinity && numB !== Infinity) {
                        return numA - numB;
                    }
                }
                return a.label.localeCompare(b.label);
            });

            nodes.forEach((node) => {
                if (node.children && node.children.length > 0) {
                    sortChildrenRecursively(node.children);
                }
            });
        };

        const groupedData = filteredMaterials.reduce((acc, material) => {
            const className =
                material.chapter?.classStandard?.name || "Uncategorized Class";
            const subjectName =
                material.chapter?.subject?.name || "Uncategorized Subject";
            const chapterName = material.chapter?.name || "Uncategorized Chapter";
            const chapterId = material.chapter?.id; // Get the actual chapter ID

            let classNode = acc.find((node) => node.id === className);
            if (!classNode) {
                classNode = {
                    id: className,
                    label: `Class: ${className}`,
                    children: [],
                };
                acc.push(classNode);
            }

            let subjectNode = classNode.children.find(
                (node) => node.id === `${className}-${subjectName}`
            );
            if (!subjectNode) {
                subjectNode = {
                    id: `${className}-${subjectName}`,
                    label: `Subject: ${subjectName}`,
                    children: [],
                };
                classNode.children.push(subjectNode);
            }

            let chapterNode = subjectNode.children.find(
                (node) => node.id === `${className}-${subjectName}-${chapterName}`
            );
            if (!chapterNode) {
                chapterNode = {
                    id: `${className}-${subjectName}-${chapterName}`,
                    label: `${chapterName}`,
                    children: [],
                    chapterId: chapterId, // Store chapter ID here
                };
                subjectNode.children.push(chapterNode);
            }

            chapterNode.children.push({
                id: material.id.toString(),
                label: material.title,
                link: material.link,
                type: material.type,
                description: material.description,
                updatedAt: material.updatedAt,
                isLeaf: true,
            });
            return acc;
        }, []);

        // POST-PROCESSING STEP: Inject the "Concepts" material into each chapter
        // Only inject if filters allow it (i.e., if 'CONCEPT' is selected or no type filter is applied).
        const shouldInjectConcept = isSearchMode
            ? false // Don't inject in Search Mode
            : type.length === 0 || type.includes("CONCEPT");

        if (shouldInjectConcept) {
            groupedData.forEach((classNode) => {
                classNode.children.forEach((subjectNode) => {
                    subjectNode.children.forEach((chapterNode) => {
                        // Check if the CONCEPT node already exists to prevent duplicates
                        const conceptNodeExists = chapterNode.children.some(
                            (child) => child.type === "CONCEPT"
                        );

                        if (!conceptNodeExists && chapterNode.chapterId) {
                            chapterNode.children.unshift({ // Add to the top of the chapter's list
                                id: `concept-${chapterNode.chapterId}`, // Unique ID for the concept node
                                label: "Concepts in this Chapter",
                                link: "", // Not an external link
                                type: "CONCEPT",
                                description: "View all associated concepts for this chapter.",
                                chapterId: chapterNode.chapterId, // Explicitly pass the chapter ID
                                isLeaf: true,
                            });
                        }
                    });
                });
            });
        }

        // Apply sorting to the entire tree
        sortChildrenRecursively(groupedData);

        return groupedData;
    }, [materials, filters, isSearchMode, isCategoricalMode]); // Depend on type filter

    const groupedMaterials = getGroupedData;
    const {
        chapters: allChapters,
        classes: allClasses,
        subjects: allSubjects,
        types: materialTypes,
    } = allFilterOptions;

    const getAllItemIds = (nodes) => {
        let ids = [];
        if (!nodes) return ids;
        nodes.forEach((node) => {
            ids.push(node.id);
            if (node.children) {
                ids = ids.concat(getAllItemIds(node.children));
            }
        });
        return ids;
    };

    useEffect(() => {
        setExpandedItems(getAllItemIds(groupedMaterials));
    }, [groupedMaterials]);

    // --- UPDATED handleClick: Extracts Chapter ID for CONCEPT type ---
    const handleClick = useCallback(
        (link, type, materialId, chapterId) => {
            // Check for material in the original list (for materials)
            const material = materials.find((m) => m.id.toString() === materialId);

            // Logic for file types that open in a new tab
            if (type === "VIDEO" || type === "VIRTUAL_LAB" || type === "PDF") {
                setActiveFile(null);
                setSelectedPath([]);
                setShowConcept(false);
                setSelectedChapterId(null); // Clear chapter ID
                window.open(link, "_blank");
                return;
            } else if (type === "CONCEPT") {
                // Logic for the new "Concepts" node
                setActiveFile(null); // Clear file viewer
                setSelectedPath([]);
                setShowConcept(true); // Show concept view
                setSelectedChapterId(chapterId); // Set the chapter ID
                return;
            }

            // Logic for other material types (e.g., SIMULATION, QUIZ, or custom embedded)
            setActiveFile(link);
            setError(false);
            setShowConcept(false);
            setSelectedChapterId(null); // Clear chapter ID

            if (window.innerWidth < 1024) {
                setIsTreeOpen(false);
            }

            // Path calculation for selection highlight
            if (material) {
                const newPath = [];
                const classId =
                    material.chapter?.classStandard?.name || "Uncategorized Class";
                const subjectId = `${classId}-${material.chapter?.subject?.name || "Uncategorized Subject"
                    }`;
                const chapterName = material.chapter?.name || "Uncategorized Chapter";
                const chapterIdPath = `${subjectId}-${chapterName}`;

                if (classId) newPath.push(classId);
                if (subjectId) newPath.push(subjectId);
                if (chapterIdPath) newPath.push(chapterIdPath);
                newPath.push(materialId);

                setSelectedPath(newPath);
            }
        },
        [materials]
    );

    const handleClearFilters = useCallback(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    const toggleFullScreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (iframeContainerRef.current) {
            iframeContainerRef.current.requestFullscreen().catch((err) => {
                console.error(
                    `Error attempting to enable full-screen mode: ${err.message}`
                );
            });
        }
    };

    // const toggleDarkMode = () => {
    //     setIsDarkMode(!isDarkMode);
    // };

    // --- Render Tree Logic (UPDATED for Concept Icon) ---
    const renderTree = (nodes) => {
        const isLeaf = nodes.isLeaf;
        const isConcept = isLeaf && nodes.type === "CONCEPT";

        let tooltipTitle =
            nodes.type + ": " + nodes.description || "No description available.";
        if (isLeaf && nodes.updatedAt) {
            const date = new Date(nodes.updatedAt);
            const formattedDate = date.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
            tooltipTitle += `\nLast Updated: ${formattedDate}`;
        }

        const isPathSelected = selectedPath.includes(nodes.id.toString()) ||
            (isConcept && nodes.chapterId && showConcept && selectedChapterId === nodes.chapterId);

        // Apply color class only to leaf nodes
        const colorClass = isLeaf ? getFileColorClass(nodes.type) : "";

        // Added an icon based on type (or a default folder/file icon)
        let Icon = DescriptionIcon;
        if (isLeaf) {
            switch (nodes.type) {
                case "PDF":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>📄</span>;
                    break;
                case "VIDEO":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>🎥</span>;
                    break;
                case "VIRTUAL_LAB":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>🧪</span>;
                    break;
                case "SIMULATION":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>🖥️</span>;
                    break;
                case "QUIZ":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>❓</span>;
                    break;
                case "CONCEPT":
                    Icon = () => <span className={`mr-2 ${colorClass}`}>💡</span>; // New icon for Concepts
                    break;
                default:
                    Icon = () => <span className={`mr-2 ${colorClass}`}>📎</span>;
                    break;
            }
        } else {
            // Use a folder icon for non-leaf nodes
            Icon = () => (
                <span className="mr-2 text-gray-400 dark:text-gray-500">📁</span>
            );
        }

        const treeItem = (
            <TreeItem
                key={nodes.id}
                itemId={nodes.id}
                // Wrapped label in a span to apply color and include icon
                label={
                    <>
                        <span
                            className={clsx("flex items-center", colorClass, {
                                "font-bold": !isLeaf,
                                "font-normal": isLeaf,
                                "font-extrabold": isConcept, // Emphasize the Concept node
                            })}
                        >
                            <Icon />
                            {nodes.label}
                        </span>
                        {/* <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300">
                            {nodes.type}
                        </span> */}
                    </>
                }
                onClick={() => {
                    if (isLeaf) {
                        // Pass the chapterId for the CONCEPT type node
                        handleClick(nodes.link, nodes.type, nodes.id, nodes.chapterId);
                    }
                }}
                className={clsx(
                    "py-1 transition-colors duration-200",
                    "hover:bg-gray-100 dark:hover:bg-gray-700",
                    "text-sm",
                    {
                        "bg-gray-200 dark:bg-gray-700": isPathSelected,
                    }
                )}
            >
                {Array.isArray(nodes.children)
                    ? nodes.children.map((node) => renderTree(node))
                    : null}
            </TreeItem>
        );

        return isLeaf ? (
            <Tooltip title={tooltipTitle} placement="right" arrow>
                {treeItem}
            </Tooltip>
        ) : (
            treeItem
        );
    };

    const isAnyFilterActive = isSearchMode || isCategoricalMode;

    // --- Render JSX (UPDATED Concept View Conditional Rendering) ---
    return (
        <div className="flex flex-col h-screen">
            {/* Top Filter Section (Unchanged) */}
            <div
                className={clsx(
                    "bg-white p-4 border-b border-gray-800 dark:bg-gray-900 dark:border-gray-700",
                    { hidden: isFullScreen }
                )}
            >
                <div className="flex flex-col">
                    {/* Header (Unchanged) */}
                    <div className="flex justify-between items-center mb-4 lg:mb-2 lg:flex-none">
                        <h2 className="text-2xl font-extrabold flex-none lg:mr-8">
                            <span className="mr-2">📚</span> Resource Library
                        </h2>
                        {/* Mobile back button */}
                        <button
                            onClick={() => setIsTreeOpen(true)}
                            className={clsx(
                                "lg:hidden p-2 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors",
                                { hidden: isTreeOpen }
                            )}
                            aria-label="Back to resources list"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-6 h-6 text-gray-700 dark:text-gray-300"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Filter Controls: Single Line (Unchanged) */}
                    <div className="flex flex-col md:flex-row gap-2 items-start md:items-stretch">
                        {/* 1. Search Filter (Unchanged) */}
                        <div
                            className={clsx(
                                "p-2 rounded-lg border-2 md:w-1/4 lg:w-1/5 min-w-[200px] flex items-center",
                                {
                                    "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20":
                                        isSearchMode,
                                    "border-gray-300 dark:border-gray-700": !isSearchMode,
                                }
                            )}
                        >
                            <Tooltip
                                title="Search by Tags or Title (Mutually Exclusive)"
                                placement="top"
                                arrow
                            >
                                <input
                                    type="text"
                                    value={filters.searchKeyword}
                                    onChange={handleSearchChange}
                                    placeholder="🔍 Search Tags/Title"
                                    className={clsx(
                                        "w-full border-none px-0 py-0 text-sm focus:outline-none focus:ring-0 dark:bg-transparent dark:text-gray-200 placeholder-gray-400",
                                        {
                                            "bg-transparent dark:bg-transparent":
                                                isSearchMode || !isCategoricalMode,
                                            "bg-gray-50 dark:bg-gray-800 cursor-not-allowed":
                                                isCategoricalMode,
                                        }
                                    )}
                                    disabled={isCategoricalMode}
                                />
                            </Tooltip>
                        </div>

                        {/* 2. Categorical Filters (Unchanged) */}
                        <div
                            className={clsx(
                                "p-2 rounded-lg border-2 md:w-3/4 lg:w-4/5 flex-grow flex flex-wrap gap-2 items-center",
                                {
                                    "border-green-500 bg-green-50/50 dark:bg-green-900/20":
                                        isCategoricalMode,
                                    "border-gray-300 dark:border-gray-700": !isCategoricalMode,
                                }
                            )}
                        >
                            <FilterListIcon className="text-green-500 mr-1 flex-none" />
                            <Tooltip
                                title="Filter by Categories (Mutually Exclusive)"
                                placement="top"
                                arrow
                            >
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 mr-1 flex-none">
                                    Categories:
                                </span>
                            </Tooltip>

                            <MultiSelectFilter
                                label="Type"
                                name="type"
                                selectedValues={filters.type}
                                onChange={handleFilterChange}
                                options={materialTypes}
                                isActive={!isSearchMode}
                            />

                            <MultiSelectFilter
                                label="Class"
                                name="class"
                                selectedValues={filters.class}
                                onChange={handleFilterChange}
                                options={allClasses}
                                isActive={!isSearchMode}
                            />

                            <MultiSelectFilter
                                label="Subject"
                                name="subject"
                                selectedValues={filters.subject}
                                onChange={handleFilterChange}
                                options={allSubjects}
                                isActive={!isSearchMode}
                            />

                            <MultiSelectFilter
                                label="Chapter"
                                name="chapter"
                                selectedValues={filters.chapter}
                                onChange={handleFilterChange}
                                options={allChapters}
                                isActive={!isSearchMode}
                            />
                        </div>
                    </div>

                    {/* Filter Summary and Clear All (Unchanged) */}
                    {isAnyFilterActive && (
                        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-2">
                                Active:
                            </span>
                            {Object.entries(filters).map(([key, value]) => {
                                if (Array.isArray(value) && value.length > 0) {
                                    const displayKey =
                                        key === "class"
                                            ? "Class"
                                            : key.charAt(0).toUpperCase() + key.slice(1);
                                    return (
                                        <Chip
                                            key={key}
                                            label={`${displayKey} (${value.length})`}
                                            onDelete={() => handleFilterChange(key, [])}
                                            deleteIcon={<CloseIcon />}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    );
                                }
                                if (key === "searchKeyword" && value) {
                                    return (
                                        <Chip
                                            key={key}
                                            label={`Search: "${value}"`}
                                            onDelete={() => handleFilterChange("searchKeyword", "")}
                                            deleteIcon={<CloseIcon />}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    );
                                }
                                return null;
                            })}
                            <button
                                onClick={handleClearFilters}
                                className="ml-auto flex items-center p-1 text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors font-medium rounded-md"
                                aria-label="Clear all active filters"
                            >
                                <ClearIcon sx={{ fontSize: 16 }} className="mr-1" />
                                Clear All
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content Area (Unchanged) */}
            <div className={clsx("flex-1 flex flex-col lg:flex-row overflow-hidden")}>
                {/* Materials List Column (Left) (Unchanged) */}
                <div
                    className={clsx(
                        "w-full lg:w-1/3 p-4 md:p-6 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700 overflow-y-auto transition-all duration-300 ease-in-out",
                        {
                            "hidden lg:block": !isTreeOpen,
                            block: isTreeOpen,
                            hidden: isFullScreen,
                        }
                    )}
                >
                    <Box className="h-full">
                        {/* No materials found state */}
                        {groupedMaterials.length === 0 && isAnyFilterActive ? (
                            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400 p-8">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-16 h-16 mb-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15.182 17.585A10.758 10.758 0 0112 19.5c-5.068 0-9.284-3.525-10.354-8.086a.75.75 0 01.378-.894c.594-.251 1.258-.376 1.954-.376H3.75c.988 0 1.8.812 1.8 1.8v1.8c0 .988-.812 1.8-1.8 1.8h-.75m-1.5-3v-1.8c0-.988.812-1.8 1.8-1.8h.75c.696 0 1.36.125 1.954.376.594-.251 1.258-.376 1.954-.376h1.8c.988 0 1.8.812 1.8 1.8v1.8c0 .988-.812 1.8-1.8 1.8h-1.5c-1.594 0-2.887 1.293-2.887 2.887V19.5"
                                    />
                                </svg>
                                <p className="mb-2 text-lg font-semibold">
                                    No materials found.
                                </p>
                                <p className="mb-4 text-sm">
                                    Try adjusting your filters or search terms.
                                </p>
                                <button
                                    onClick={handleClearFilters}
                                    className="px-6 py-2 text-base font-medium rounded-lg transition-colors duration-200 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        ) : (
                            <SimpleTreeView
                                aria-label="resources tree view"
                                expandedItems={expandedItems}
                                onExpandedItemsChange={(event, itemIds) =>
                                    setExpandedItems(itemIds)
                                }
                                className="h-full overflow-y-auto text-gray-900 dark:text-gray-100"
                            >
                                {groupedMaterials.map((node) => renderTree(node))}
                            </SimpleTreeView>
                        )}
                    </Box>
                </div>
                {/* Main Content Viewer (Right) */}
                <main
                    className={clsx(
                        "flex-1 p-4 md:p-6 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 transition-all duration-300 ease-in-out",
                        {
                            "p-0": isFullScreen,
                            "hidden lg:flex": isTreeOpen,
                            flex: !isTreeOpen,
                        }
                    )}
                >
                    <div
                        ref={iframeContainerRef}
                        className={clsx(
                            "w-full h-full border border-gray-300 rounded-lg shadow-xl overflow-hidden relative bg-white dark:bg-gray-800 dark:border-gray-700",
                            { "rounded-none border-none shadow-none": isFullScreen }
                        )}
                    >
                        {(activeFile || showConcept) ? (
                            <>
                                {/* Conditional Concept View - Now depends on selectedChapterId */}
                                {showConcept && selectedChapterId && (
                                    <ConceptView chapterId={selectedChapterId} allChapters={chapters} navigateTo={navigateTo} />
                                )}

                                {/* Only show iframe if a file is active and we are NOT in the concept view */}
                                {activeFile && !showConcept && (
                                    <>
                                        <iframe
                                            src={activeFile}
                                            frameBorder="0"
                                            width="100%"
                                            height="100%"
                                            allowFullScreen
                                            title="Resource Viewer"
                                            className="z-10"
                                        ></iframe>
                                        <div className="absolute top-2 right-2 flex space-x-2 z-50">
                                            <button
                                                onClick={toggleFullScreen}
                                                className="p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 shadow-md text-gray-700 transition-colors dark:bg-gray-700 dark:bg-opacity-80 dark:hover:bg-opacity-100 dark:text-gray-300"
                                                aria-label="Toggle full screen"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={1.5}
                                                    stroke="currentColor"
                                                    className="w-6 h-6"
                                                >
                                                    {isFullScreen ? (
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M9 9l6-6m0 0l6 6m-6-6v6m6 6l-6 6m0 0l-6-6m6 6v-6m-6 6H6m0 0v-6m0 6l6-6m0 0v6M9 9l-6 6m0 0h6m0-6v6"
                                                        />
                                                    ) : (
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M3.75 3.75v3h3m-3-3l6-6m0 0v6M20.25 3.75v3h-3m3-3l-6-6m0 0v6M3.75 20.25v-3h3m-3 3l6-6m0 0v-6M20.25 20.25v-3h-3m3 3l-6-6m0 0v-6"
                                                        />
                                                    )}
                                                </svg>
                                            </button>
                                        </div>
                                        <AnnotationOverlay
                                            containerRef={iframeContainerRef}
                                            isFullScreen={isFullScreen}
                                        />
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 text-center p-8">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-16 h-16 mb-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 8.25l-.946-.886a1.125 1.125 0 010-1.588l.946-.886m2.138-1.46c.3-.393.18-1.025-.26-1.396.44.37.56.703.26 1.096"
                                    />
                                </svg>
                                <p className="text-xl font-semibold mb-2">
                                    Welcome to the Resource Library
                                </p>
                                <p className="text-sm">
                                    Select a file or the **💡 Concepts** option from the tree structure
                                    on the left to view content or concepts here.
                                </p>
                            </div>
                        )}
                        {error && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-95 dark:bg-gray-800 dark:bg-opacity-95 text-red-500 text-lg z-20">
                                <span className="p-6 bg-white rounded-xl shadow-lg border border-red-400 dark:bg-gray-700 dark:border-red-600 text-center">
                                    <span className="text-3xl mb-2 block">⚠️</span>
                                    Document failed to load. Please try another resource or check
                                    the link.
                                </span>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

// --- Concept View Component (UPDATED to use local chapter data) ---
const ConceptView = ({ chapterId, allChapters, navigateTo }) => {
    console.log(chapterId, allChapters)

    // 💡 Use useMemo to efficiently find the chapter and extract concepts
    const concepts = useMemo(() => {
        if (!chapterId || !Array.isArray(allChapters)) {
            return [];
        }

        // 1. Find the corresponding chapter using the ID
        const selectedChapter = allChapters.find(
            (chapter) => chapter.id.toString() == chapterId
        );

        if (!selectedChapter || !Array.isArray(selectedChapter.conceptChapters)) {
            return [];
        }

        // 2. Map the nested structure to a flat list of concepts
        return selectedChapter.conceptChapters
            .map((cc) => cc.concept)
            .filter((concept) => concept && concept.name); // Filter out any null/undefined concepts
    }, [chapterId, allChapters]);

    const chapterName = useMemo(() => {
        const selectedChapter = allChapters.find(
            (chapter) => chapter.id.toString() == chapterId
        );
        return selectedChapter?.name || `Chapter ID: ${chapterId}`;
    }, [chapterId, allChapters]);

    return (
        <div className="p-6 h-full overflow-y-auto w-full dark:text-gray-100">
            <h3 className="text-2xl font-bold mb-4 text-yellow-600 dark:text-yellow-400 border-b pb-2">
                {chapterName}
            </h3>
            {concepts.length > 0 ? (
                <ul className="space-y-4">
                    {concepts.sort((a, b) => a.code.localeCompare(b.code)).map((concept, index) => (
                        <li
                            key={concept.id}
                            onClick={(event) => {
                                navigateTo("conceptDetails", concept.code);
                            }}
                            className="p-4 cursor-pointer border border-blue-200 rounded-lg shadow-md dark:border-blue-700/50 bg-blue-50 dark:bg-gray-800 transition-colors"
                        >
                            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                                <span className="mr-3 text-blue-600 dark:text-blue-400">#</span>
                                {concept.name}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 pl-6">
                                {concept.description || "No description provided."}
                            </p>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                    <p className="text-lg font-semibold">
                        This chapter has no associated concepts.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ResourcesContent;